import React, { CSSProperties } from 'react';
import styled from 'styled-components';

export type Padding = 0 | 4 | 8 | 12 | 16 | 20 | 24 | 32;

type Props = {
  readonly className?: string;
  readonly children?: React.ReactNode;

  readonly basis?: CSSProperties['flexBasis'];
  readonly grow?: true | CSSProperties['flexGrow'];
  readonly shrink?: CSSProperties['flexShrink'];
  readonly gap?: Padding | `${Padding}px ${Padding}px`;
  readonly wrap?: true | CSSProperties['flexWrap'];
  readonly direction?: CSSProperties['flexDirection'];

  readonly align?: CSSProperties['alignItems'];
  readonly justify?: CSSProperties['justifyContent'];
  readonly alignSelf?: CSSProperties['alignSelf'];

  readonly padding?:
    | Padding
    | `${Padding}px ${Padding}px`
    | `${Padding}px ${Padding}px ${Padding}px ${Padding}px`;

  readonly onClick?: React.MouseEventHandler<HTMLDivElement>;
};

type StyledProps = {
  readonly $gap: Props['gap'];
  readonly $direction: Props['direction'];
  readonly $wrap: Props['wrap'];
  readonly $basis: Props['basis'];
  readonly $grow: Props['grow'];
  readonly $shrink: Props['shrink'];
  readonly $align: Props['align'];
  readonly $alignSelf: Props['alignSelf'];
  readonly $justify: Props['justify'];
  readonly $padding: Props['padding'];
};

const StyledWrapper = styled.div.attrs<StyledProps>(
  ({
    $gap,
    $direction,
    $wrap,
    $basis,
    $grow,
    $shrink,
    $align,
    $alignSelf,
    $justify,
    $padding,
  }) => ({
    style: {
      flexBasis: $basis,
      flexGrow: $grow === true ? 1 : $grow,
      flexShrink: $shrink,
      gap: $gap,
      alignItems: $align,
      alignSelf: $alignSelf,
      justifyContent: $justify,
      flexWrap: $wrap === true ? 'wrap' : $wrap,
      flexDirection: $direction,
      padding: $padding,
    } as CSSProperties,
  }),
)<StyledProps>`
  display: flex;
`;

export const Flex = ({
  align,
  alignSelf,
  basis,
  children,
  className,
  direction,
  gap,
  grow,
  justify,
  onClick,
  padding,
  shrink,
  wrap,
}: Props) => (
  <StyledWrapper
    $align={align}
    $alignSelf={alignSelf}
    $basis={basis}
    $direction={direction}
    $gap={gap}
    $grow={grow}
    $justify={justify}
    $padding={padding}
    $shrink={shrink}
    $wrap={wrap}
    className={className}
    onClick={onClick}
  >
    {children}
  </StyledWrapper>
);
